'use client';

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth';
import { useAuth } from '@/lib/contexts/auth-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { toast } from 'sonner';
import { Building2, Check } from 'lucide-react';
import { apiErrorMessage } from '@/lib/api/errors';

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTenantDialog({ open, onOpenChange }: CreateTenantDialogProps) {
  const { loginWithTokens } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [created, setCreated] = useState(false);

  const mutation = useMutation({
    mutationFn: (tenantName: string) => authApi.createTenant({ name: tenantName }),
    onSuccess: async (data) => {
      // Update tokens to switch to new tenant
      await loginWithTokens(data.access_token, data.refresh_token);
      setCreated(true);
      toast.success(data.message);
      // Invalidate queries
      queryClient.invalidateQueries();
      setTimeout(() => {
        handleClose();
        // Reload to reset all state
        window.location.reload();
      }, 1500);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to create organization'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter an organization name');
      return;
    }
    mutation.mutate(name);
  };

  const handleClose = () => {
    setName('');
    setCreated(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        {created ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="mb-4 rounded-full bg-green-100 p-3">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <DialogTitle className="text-center mb-2">Organization Created!</DialogTitle>
            <DialogDescription className="text-center">
              Switching to your new organization...
            </DialogDescription>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Building2 className="mr-2 h-5 w-5" />
                Create New Organization
              </DialogTitle>
              <DialogDescription>
                Create a new organization to manage separate projects or teams.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <FloatingLabelInput
                    id="name"
                    label="Organization Name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={mutation.isPending}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Creating...' : 'Create Organization'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
